import { useCallback, useEffect, useRef } from 'react';
import { isNearStreamBottom, isUpwardStreamScroll } from '../utils/streamScroll';

// Follow streamed content while the reader is already at the end. Once they
// scroll upward, preserve their reading position until they return near the
// bottom. A new generation opts back into following from its loading state.
export default function useStreamingAutoScroll({ status, content }) {
  const containerRef = useRef(null);
  const followRef = useRef(true);
  const previousStatusRef = useRef(status);
  const lastScrollTopRef = useRef(0);
  const lastTouchYRef = useRef(null);

  const onScroll = useCallback(event => {
    const element = event.currentTarget;
    if (isUpwardStreamScroll(lastScrollTopRef.current, element.scrollTop)) {
      followRef.current = false;
    } else if (isNearStreamBottom(element)) {
      followRef.current = true;
    }
    lastScrollTopRef.current = element.scrollTop;
  }, []);

  const onWheel = useCallback(event => {
    if (event.deltaY < 0) followRef.current = false;
  }, []);

  const onTouchStart = useCallback(event => {
    lastTouchYRef.current = event.touches[0]?.clientY ?? null;
  }, []);

  const onTouchMove = useCallback(event => {
    const nextY = event.touches[0]?.clientY;
    if (nextY == null) return;
    if (lastTouchYRef.current != null && nextY > lastTouchYRef.current + 1) {
      followRef.current = false;
    }
    lastTouchYRef.current = nextY;
  }, []);

  const onKeyDown = useCallback(event => {
    if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) followRef.current = false;
  }, []);

  useEffect(() => {
    if (['building', 'correcting'].includes(status)
        && previousStatusRef.current !== status) {
      followRef.current = true;
      lastScrollTopRef.current = containerRef.current?.scrollTop || 0;
    }
    previousStatusRef.current = status;
  }, [status]);

  useEffect(() => {
    const busy = ['building', 'streaming', 'correcting'].includes(status);
    if (!busy || !followRef.current || !containerRef.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
    lastScrollTopRef.current = containerRef.current.scrollTop;
  }, [content, status]);

  return { containerRef, onScroll, onWheel, onTouchStart, onTouchMove, onKeyDown };
}
